import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { messages } from '../messages';
import { ApiClient } from '../api/client';
import { CreationFlowHeader } from '../components/creation/CreationFlowHeader';
import WebApp from '@twa-dev/sdk';
import { UserInfo } from '../types';
import { KovrificationConfig, CreationStrategy } from '../types/creation';
import { StrategyType } from '../constants/strategy';
import { KovrificationCreationConfig } from '../components/strategies/KovrificationCreationConfig';
import { BestMomentsCreationConfig, BestMomentsConfigRequestData } from '../components/strategies/BestMomentsCreationConfig';
import posthog from 'posthog-js';
import { useTranslation } from '../i18n';

// Import template images no longer needed here as they're used directly in the KovrificationCreationConfig

interface ConfigureCreationPageProps {
  creationId: string;
  creationTypeName: string;
  userInfo: UserInfo;
  initialConfig: KovrificationConfig;
  creationStrategy: CreationStrategy;
  strategyType: StrategyType;
  onBack: () => void;
  onComplete: (newBalance: number) => void;
}

export function ConfigureCreationPage({
  creationId,
  creationTypeName,
  userInfo,
  initialConfig,
  creationStrategy,
  strategyType,
  onBack,
  onComplete
}: ConfigureCreationPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [kovrConfig, setKovrConfig] = useState<KovrificationConfig>({
    ...initialConfig,
    caption: {
      ...initialConfig.caption,
      style_id: initialConfig.caption.style_id || 'karaoke_default_v1'
    }
  });
  
  const [bestMomentsConfig, setBestMomentsConfig] = useState<BestMomentsConfigRequestData>(() => {
    // Initialize from creation strategy if available
    return {
      captions_enabled: creationStrategy.captions_enabled !== undefined ? creationStrategy.captions_enabled : true,
      caption_config: {
        color: creationStrategy.caption_config?.color,
        accent_color: creationStrategy.caption_config?.accent_color,
        font_size: creationStrategy.caption_config?.font_size,
        accent_font_size: creationStrategy.caption_config?.accent_font_size,
        words_per_line: creationStrategy.caption_config?.words_per_line,
        vertical_margin: creationStrategy.caption_config?.vertical_margin,
        style_id: creationStrategy.caption_config?.style_id || 'karaoke_default_v1'
      }
    };
  });
  
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [isPriceFetching, setIsPriceFetching] = useState(false);

  const fetchCalculatedPrice = useCallback(async () => {
    if (isPriceFetching) return;

    setIsPriceFetching(true);
    try {
      const data = await ApiClient.getCreationPrice(creationId);
      setCalculatedPrice(data.price);
    } catch (error) {
      console.error('Error fetching price:', error);
    } finally {
      setIsPriceFetching(false);
    }
  }, [creationId, isPriceFetching]);

  // Fetch calculated price for best moments strategy
  useEffect(() => {
    if (strategyType === StrategyType.BEST_MOMENTS && !calculatedPrice && !isPriceFetching) {
      fetchCalculatedPrice();
    }
  }, [strategyType, calculatedPrice, isPriceFetching, creationId, fetchCalculatedPrice]);

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConfigSave = async () => {
    try {
      // Handle different strategy types
      if (strategyType === StrategyType.KOVRIFICATION) {
        // For Kovrification strategy
        if (kovrConfig.caption.enabled) {
          // Only include properties that are defined
          const captionConfig: Record<string, unknown> = {};
          
          if (kovrConfig.caption.style_id !== undefined) {
            captionConfig.style_id = kovrConfig.caption.style_id;
          }
          if (kovrConfig.caption.color !== undefined) {
            captionConfig.color = kovrConfig.caption.color;
          }
          if (kovrConfig.caption.accent_color !== undefined) {
            captionConfig.accent_color = kovrConfig.caption.accent_color;
          }
          if (kovrConfig.caption.font_size !== undefined) {
            captionConfig.font_size = kovrConfig.caption.font_size;
          }
          if (kovrConfig.caption.accent_font_size !== undefined) {
            captionConfig.accent_font_size = kovrConfig.caption.accent_font_size;
          }
          if (kovrConfig.caption.words_per_line !== undefined) {
            captionConfig.words_per_line = kovrConfig.caption.words_per_line;
          }
          if (kovrConfig.caption.vertical_margin !== undefined) {
            captionConfig.vertical_margin = kovrConfig.caption.vertical_margin;
          }

          await ApiClient.configureKovrification(creationId, {
            video_processing: kovrConfig.video_processing,
            caption_config: captionConfig,
            captions_enabled: true
          });
        } else {
          // Captions are disabled
          await ApiClient.configureKovrification(creationId, {
            video_processing: kovrConfig.video_processing,
            captions_enabled: false
          });
        }
      } else if (strategyType === StrategyType.BEST_MOMENTS) {
        // For Best Moments strategy
        if (bestMomentsConfig.captions_enabled) {
          // Only include properties that are defined
          const captionConfig: Record<string, unknown> = {};
          
          if (bestMomentsConfig.caption_config.style_id !== undefined) {
            captionConfig.style_id = bestMomentsConfig.caption_config.style_id;
          }
          if (bestMomentsConfig.caption_config.color !== undefined) {
            captionConfig.color = bestMomentsConfig.caption_config.color;
          }
          if (bestMomentsConfig.caption_config.accent_color !== undefined) {
            captionConfig.accent_color = bestMomentsConfig.caption_config.accent_color;
          }
          if (bestMomentsConfig.caption_config.font_size !== undefined) {
            captionConfig.font_size = bestMomentsConfig.caption_config.font_size;
          }
          if (bestMomentsConfig.caption_config.accent_font_size !== undefined) {
            captionConfig.accent_font_size = bestMomentsConfig.caption_config.accent_font_size;
          }
          if (bestMomentsConfig.caption_config.words_per_line !== undefined) {
            captionConfig.words_per_line = bestMomentsConfig.caption_config.words_per_line;
          }
          if (bestMomentsConfig.caption_config.vertical_margin !== undefined) {
            captionConfig.vertical_margin = bestMomentsConfig.caption_config.vertical_margin;
          }

          await ApiClient.configureBestMoments(creationId, {
            captions_enabled: true,
            caption_config: captionConfig
          });
        } else {
          // Captions are disabled
          await ApiClient.configureBestMoments(creationId, {
            captions_enabled: false
          });
        }
      }

      // Start processing
      await ApiClient.startProcessing(creationId);
      // PostHog: Capture creation_started event
      posthog.capture('creation_started', { creation_id: creationId, strategy_type: strategyType });

      // Get updated user info to reflect new balance
      const updatedUserInfo = await ApiClient.getUserInfo();
      onComplete(updatedUserInfo.balance);

      // Navigate to creation details page to view processing status
      navigate(`/creation/${creationId}`);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      // Show error to user
      const tg = WebApp;
      await tg.showPopup({
        title: t('configureCreationPage.popupErrorTitle'),
        message: t('configureCreationPage.popupErrorMessage'),
        buttons: [{ type: 'ok' }]
      });
    }
  };

  // Render strategy-specific configuration UI
  const renderStrategyConfig = () => {
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return (
          <KovrificationCreationConfig 
            config={kovrConfig} 
            onConfigChange={setKovrConfig} 
          />
        );
      case StrategyType.BEST_MOMENTS:
        return (
          <BestMomentsCreationConfig 
            config={bestMomentsConfig} 
            onConfigChange={setBestMomentsConfig} 
          />
        );
      // Add more cases here as more strategies are added
      default:
        return null;
    }
  };

  // Get the strategy-specific title
  const getStrategyTitle = () => {
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return t('configureCreationPage.strategyTitleCroll');
      case StrategyType.BEST_MOMENTS:
        return t('configureCreationPage.strategyTitleBestMoments');
      default:
        return creationTypeName;
    }
  };

  // Check if the user has enough balance
  const hasEnoughBalance = strategyType === StrategyType.BEST_MOMENTS 
    ? (calculatedPrice === null || userInfo.balance >= calculatedPrice)
    : userInfo.balance >= creationStrategy.price;

  return (
    <div className="create-flow">
      <CreationFlowHeader 
        title={getStrategyTitle()}
        onBack={onBack}
      />

      <div className="configuration-screen p-4 md:p-6 lg:p-8 xl:p-10 w-full">
        {/* Main content container with responsive layout */}
        <div className="w-full xl:flex xl:flex-row xl:items-start xl:gap-8">
          {/* Left side - Balance status and configuration */}
          <div className="w-full xl:flex-grow">
            {/* Balance Status */}
            <div className="mb-6 p-4 rounded-xl bg-[rgba(36,129,204,0.04)] dark:bg-[#243242] shadow-[0_2px_8px_-2px_rgba(36,129,204,0.15)] dark:border dark:border-white/[0.08]">
              {strategyType === StrategyType.BEST_MOMENTS ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-theme-text">
                      <span className="material-symbols-outlined text-[#2D62FF]">payments</span>
                      <span>{t('configureCreationPage.processingPriceLabel')}</span>
                    </div>
                    {isPriceFetching ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-theme-hint">{t('configureCreationPage.calculatingPriceStatus')}</span>
                      </div>
                    ) : calculatedPrice !== null ? (
                      <div className="flex items-center gap-1 font-medium">
                        <span className="text-lg">✨</span>
                        <span>{calculatedPrice}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-theme-hint">
                        <span className="text-lg">✨</span>
                        <span>{t('configureCreationPage.defaultPriceDisplay')}</span>
                      </div>
                    )}
                  </div>
                  
                  {userInfo.balance > 0 && (
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-theme-text">{t('configureCreationPage.yourBalanceLabel')}</span>
                      <div className="flex items-center gap-1 font-medium">
                        <span className="text-lg">✨</span>
                        <span>{userInfo.balance}</span>
                      </div>
                    </div>
                  )}
                  
                  {calculatedPrice !== null && userInfo.balance < calculatedPrice && (
                    <div className="flex items-center gap-2 text-amber-500 dark:text-amber-400 mt-2">
                      <span className="material-symbols-outlined">warning</span>
                      <span>{t('configureCreationPage.needsMoreSparklesText', { amount: calculatedPrice - userInfo.balance })}</span>
                    </div>
                  )}
                </div>
              ) : userInfo.balance >= creationStrategy.price ? (
                <div className="flex items-center gap-2 text-emerald-500 dark:text-emerald-400">
                  <span className="material-symbols-outlined">check_circle</span>
                  <span>{t('configureCreationPage.enoughSparklesText')}</span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="text-gray-800 dark:text-white">{t('configureCreationPage.processingPriceLabel')} <span className="text-lg">✨</span>{creationStrategy.price}</div>
                  <div className="text-amber-500 dark:text-amber-400">
                    {t('configureCreationPage.needsMoreSparklesText', { amount: creationStrategy.price - userInfo.balance })}
                  </div>
                </div>
              )}
            </div>

            {/* Configuration UI */}
            <div className="w-full mb-6 xl:mb-0">
              {renderStrategyConfig()}
            </div>
          </div>

          {/* Right side - Process Button (appears to the side on xl screens) */}
          <div className="w-full xl:w-64 xl:flex-shrink-0 xl:sticky xl:top-8">
            <button 
              onClick={handleConfigSave}
              disabled={!hasEnoughBalance}
              className="w-full py-3 px-4 bg-blue-500 text-white font-medium text-sm rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {!hasEnoughBalance ? 
                t('configureCreationPage.insufficientBalanceButton') : 
                messages.create.buttons.continue
              }
            </button>
            
            {/* Balance indicator on xl screens for easy access */}
            <div className="hidden xl:flex xl:items-center xl:justify-between xl:mt-4 xl:p-3 xl:bg-gray-50 xl:dark:bg-gray-800 xl:rounded-lg">
              <span className="text-sm text-theme-text">{t('configureCreationPage.yourBalanceLabel')}</span>
              <div className="flex items-center gap-1 font-medium">
                <span className="text-lg">✨</span>
                <span>{userInfo.balance}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 